import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Calendar, Clock, Target, Activity, BarChart2,
  Brain, ChevronDown, ChevronUp, Play, Tag, StickyNote, Shield, Share2,
  X, Loader, TrendingUp, TrendingDown, Globe, BookOpen,
  AlertTriangle, CheckCircle2, XCircle, Flame, Info,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import TradeReplay from '../components/charts/TradeReplay';
import { formatPrice, formatDate } from '../utils/formatters';
import ShareToForumModal from '../components/ShareToForumModal';
import TradeShareCard_v2 from '../components/TradeShareCard_v2';
import { DetailPageSkeleton } from '../components/SkeletonLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const cr  = v  => v  != null ? `${v  >= 0 ? '+' : ''}${(Math.abs(v)  / 1e7).toFixed(0)} Cr` : '—';
const pct = (v, d = 2) => v != null ? `${parseFloat(v) >= 0 ? '+' : ''}${parseFloat(v).toFixed(d)}%` : '—';

// ── sub-components ────────────────────────────────────────────────────────────
function Divider({ label }) {
  const { colors } = useTheme();
  return (
    <div className="flex items-center gap-3 pt-1">
      <div className="flex-1 h-px" style={{ background: colors.border }} />
      <span className="text-[10px] font-bold tracking-widest px-2" style={{ color: colors.secondaryText }}>{label}</span>
      <div className="flex-1 h-px" style={{ background: colors.border }} />
    </div>
  );
}

function Card({ children, accent, className = '' }) {
  const { colors } = useTheme();
  return (
    <div className={`rounded-2xl p-4 ${className}`}
      style={{ background: accent ? `linear-gradient(135deg,${accent}12 0%,${colors.cardBg} 100%)` : colors.cardBg,
               border: `1px solid ${accent ? `${accent}28` : colors.border}`,
               boxShadow: colors.cardShadow }}>
      {children}
    </div>
  );
}

function SecHead({ icon: Icon, title, subtitle, color }) {
  const { colors } = useTheme();
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color || colors.accent}22` }}>
        <Icon className="w-3.5 h-3.5" style={{ color: color || colors.accent }} />
      </div>
      <div>
        <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>{title}</p>
        {subtitle && <p className="text-[10px]" style={{ color: colors.secondaryText }}>{subtitle}</p>}
      </div>
    </div>
  );
}

function MiniCell({ label, value, color }) {
  const { colors } = useTheme();
  return (
    <div className="p-2.5 rounded-xl" style={{ background: color ? `${color}10` : colors.inputBg, border: `1px solid ${color ? `${color}25` : colors.border}` }}>
      <p className="text-[10px]" style={{ color: colors.secondaryText }}>{label}</p>
      <p className="text-[13px] font-bold mt-0.5 leading-tight" style={{ color: color || colors.primaryText }}>{value ?? '—'}</p>
    </div>
  );
}

function CheckRow({ label, ok, value }) {
  const { colors } = useTheme();
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.profit }} />
             : <XCircle     className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.loss }} />}
        <span className="text-[12px]" style={{ color: colors.primaryText }}>{label}</span>
      </div>
      {value != null && <span className="text-[12px] font-semibold" style={{ color: ok ? colors.profit : colors.loss }}>{value}</span>}
    </div>
  );
}

function TakeawayBox({ text, color }) {
  const { colors } = useTheme();
  if (!text) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl mt-2"
      style={{ background: `${color || colors.accent}10`, border: `1px solid ${color || colors.accent}25` }}>
      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: color || colors.accent }} />
      <p className="text-[11px] leading-relaxed font-medium" style={{ color: colors.primaryText }}>{text}</p>
    </div>
  );
}

// ── main ──────────────────────────────────────────────────────────────────────
export default function TradeDetailPage() {
  const { tradeId } = useParams();
  const navigate    = useNavigate();
  const { colors }  = useTheme();
  const { isAdmin, canAccess } = usePermissions();
  const canSocialShare = isAdmin || canAccess('social:share');

  const [trade,      setTrade]      = useState(null);
  const [notFound,   setNotFound]   = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [aiReview,   setAiReview]   = useState(null);
  const [showReplay, setShowReplay] = useState(false);
  const [shareOpen,  setShareOpen]  = useState(false);
  const [shareSnap,  setShareSnap]  = useState(null);
  const [socialOpen, setSocialOpen] = useState(false);
  const [capturing,  setCapturing]  = useState(false);
  const [globalExp,  setGlobalExp]  = useState(false);
  const shareCardRefV2 = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/trade/${tradeId}/analysis`).then(r => {
      const d = r.data;
      const entryStr = d.entry_date ? d.entry_date.split('T')[0] : null;
      const exitStr  = d.exit_date  ? d.exit_date.split('T')[0]  : null;
      setTrade({
        id:           d.trade_id,
        symbol:       d.symbol,
        company:      d.company,
        buyPrice:     d.entry_price,
        sellPrice:    d.exit_price,
        quantity:     d.quantity,
        pnl:          d.pnl,
        pnlPct:       d.pnl_pct,
        entryDate:    entryStr,
        exitDate:     exitStr,
        entryDateIso: d.entry_date || null,  // full ISO with time for share card
        exitDateIso:  d.exit_date  || null,
        holdingDays:  exitStr && entryStr ? Math.max(1, Math.round((new Date(exitStr)-new Date(entryStr))/86400000)) : null,
        status:       d.status || 'OPEN',
        strategyTags: d.strategy_tags || [],
        notes:        d.notes || null,
        stopLoss:     d.stop_loss  || null,
        targetPrice:  d.target_price || null,
        tradeVariant: d.product_type || 'SWING',
        productType:  d.product_type || null,
        actualR:      d.actual_r != null ? parseFloat(d.actual_r) : null,
        exitReason:   d.exit_reason || null,
        disciplineSnap: d.discipline_snap || null,
        regime:       d.regime        || null,
        globalIndices:d.global_indices || [],
        fiiDii:       d.fii_dii       || null,
        sentiment:    d.market_sentiment || null,
        symbolCtx:    d.symbol_context || null,
        foContract:   d.fo_contract   || null,
        checklist:    d.checklist     || null,
        userProfile:  d.user_profile  || null,
        tradingStats:  d.trading_stats  || null,
        simDate:       d.sim_date       || null,
        actualLossPct: d.actual_loss_pct != null ? parseFloat(d.actual_loss_pct) : null,
        deployedPct:   d.deployed_pct   != null ? parseFloat(d.deployed_pct)   : null,
        isLongOption:  d.is_long_option || false,
      });
      setAiReview(d.ai_review || null);
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [tradeId]);

  const buildSnap = () => {
    const exit = parseFloat(trade.sellPrice || 0);
    const sl   = trade.stopLoss    ? parseFloat(trade.stopLoss)    : null;
    const tgt  = trade.targetPrice ? parseFloat(trade.targetPrice) : null;
    const outcome = trade.exitReason === 'EOD'    ? 'eod'
                  : trade.exitReason === 'SL'     ? 'sl_hit'
                  : trade.exitReason === 'TARGET' ? 'target_hit'
                  : 'manual';
    return {
      // full ISO timestamps so card can derive entry/exit times
      market_context:  trade.regime ? { sim_date: trade.simDate, regime: trade.regime, global_indices: trade.globalIndices, fii_dii: trade.fiiDii, market_sentiment: trade.sentiment } : null,
      user_decision:   { reason: trade.notes, confidence: trade.checklist?.confidence },
      trade_setup:     { symbol: (trade.symbol||'').replace('.NS',''), company: trade.company, trade_type: 'BUY', entry_price: parseFloat(trade.buyPrice||0), stop_loss: sl, target_price: tgt, quantity: trade.quantity, strategy_tags: trade.strategyTags, entry_date: trade.entryDateIso || trade.entryDate },
      actual_results:  { exit_price: exit, exit_date: trade.exitDateIso || trade.exitDate, pnl: trade.pnl, pnl_pct: trade.pnlPct, holding_days: trade.holdingDays, outcome },
      discipline_snap: trade.disciplineSnap,
      symbol_context:  trade.symbolCtx,
      checklist:       trade.checklist,
      fo_contract:     trade.foContract,
      ai_review:       aiReview,
      // v2 extras
      product_type:    trade.productType || trade.tradeVariant,
      sim_date:        trade.simDate,
      total_charges:   trade.totalCharges || null,
    };
  };

  if (loading)           return <DetailPageSkeleton />;
  if (notFound || !trade) return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="p-2 rounded-lg" style={{ background: `${colors.accent}12` }}>
        <ArrowLeft className="w-5 h-5" style={{ color: colors.primaryText }} />
      </button>
      <p className="text-center text-sm py-16" style={{ color: colors.secondaryText }}>Trade not found</p>
    </div>
  );

  const isClosed = trade.status === 'CLOSED';
  const isProfit = (trade.pnl || 0) >= 0;
  const sym      = (trade.symbol || '').replace('.NS', '');
  const regime   = trade.regime   || {};
  const fii      = trade.fiiDii   || {};
  const disc     = trade.disciplineSnap || {};
  const checklist = trade.checklist    || {};
  const de        = checklist.decision_engine || {};
  const scanners  = (trade.symbolCtx || {}).scanner_signals || [];
  const combo     = regime.combo_stats || {};
  const prof      = (trade.userProfile || {}).risk_profile || {};
  const rr        = disc.rr_ratio;

  // exit quality
  const exitQuality = (() => {
    if (!isClosed || !trade.targetPrice || !trade.sellPrice) return null;
    if (trade.exitReason === 'EOD') return { label: 'EOD Exit', color: '#64748b', detail: 'Intraday position closed at end of day.' };
    const exit = parseFloat(trade.sellPrice), tgt = parseFloat(trade.targetPrice);
    const qty  = trade.quantity || 1;
    const tol  = tgt * 0.02;
    const isShortOption = trade.foContract && ['SHORT_CE','SHORT_PE','SELL_CE','SELL_PE'].includes((trade.foContract.contract_type||'').toUpperCase());
    if (Math.abs(exit - tgt) <= tol)                           return { label: 'Clean Exit',      color: '#16C784', detail: 'Exited within 2% of target.' };
    if (!isShortOption && exit < tgt - tol)                    return { label: 'Early Exit',       color: '#f59e0b', detail: `Left ₹${Math.abs((tgt-exit)*qty).toLocaleString('en-IN',{maximumFractionDigits:0})} on the table.` };
    if (!isShortOption && exit > tgt + tol && isProfit)        return { label: 'Held Past Target', color: '#6366F1', detail: 'Went beyond target — added reversal risk.' };
    if (!isShortOption && exit > tgt + tol && !isProfit)       return { label: 'Greed Exit',       color: '#ef4444', detail: 'Held past target, price reversed.' };
    return null;
  })();

  // regime colours
  const gapColor = { strong_up: colors.profit, mild_up: '#22d3ee', strong_down: colors.loss, mild_down: '#f97316', flat: colors.secondaryText }[regime.gap_type] || colors.secondaryText;
  const biasColor = { bull: colors.profit, bear: colors.loss }[regime.global_bias] || colors.secondaryText;
  const comboColor= { follow: colors.profit, fade: colors.loss, wait: '#f59e0b' }[combo.mode] || colors.secondaryText;
  const deGradeColor = { A: colors.profit, B: '#22d3ee', C: '#f59e0b', D: colors.loss }[de.grade] || colors.secondaryText;

  // split indices by timing
  const prevDayIndices   = (trade.globalIndices||[]).filter(i => ['US','Europe'].includes(i.region));
  const morningIndices   = (trade.globalIndices||[]).filter(i => ['Asia','Macro'].includes(i.region));

  // FII takeaway
  const fiiTakeaway = (() => {
    const fn = fii.fii_net, dn = fii.dii_net;
    if (fn == null && dn == null) return null;
    const fiiDir = fn >= 0 ? 'buying' : 'selling';
    const diiDir = dn >= 0 ? 'buying' : 'selling';
    if (fn < 0 && dn < 0) return `Both FII and DII were ${fiiDir === 'selling' ? 'net sellers' : 'net buyers'} — broad institutional ${fn < 0 ? 'exit' : 'accumulation'} on entry day.`;
    if (fn < 0 && dn >= 0) return `FII sold while DII bought — diverging institutional views. DII often acts as a cushion against FII outflows.`;
    if (fn >= 0 && dn < 0) return `FII bought while DII sold — foreign inflows but domestic selling. Watch whether foreign interest sustains.`;
    return `Both FII and DII were net buyers — strong institutional support on entry day.`;
  })();

  // global cues takeaway
  const globalTakeaway = (() => {
    if (!prevDayIndices.length) return null;
    const avg = prevDayIndices.reduce((s, i) => s + parseFloat(i.change_pct||0), 0) / prevDayIndices.length;
    if (avg > 0.5)  return `US and European markets closed positively (avg ${avg.toFixed(2)}%) — supportive global backdrop for your entry.`;
    if (avg < -0.5) return `US and European markets closed weak (avg ${avg.toFixed(2)}%) — global headwind on entry day.`;
    return `US and European markets were largely flat — no strong directional global cue on entry day.`;
  })();

  // risk adherence — values computed by backend via map_trade_for_risk (single source of truth)
  const riskChecks = (() => {
    const items = [];
    const sl     = trade.stopLoss ? parseFloat(trade.stopLoss) : null;
    const riskPct = prof.recommended_risk_pct ? parseFloat(prof.recommended_risk_pct) : null;
    const minRR  = prof.preferred_min_rr ? parseFloat(prof.preferred_min_rr) : 1.5;

    // 1. Stop loss presence
    if (!sl) {
      items.push({ label: 'No stop loss set', sub: 'Unlimited downside — violates risk rules', ok: false });
    } else {
      const lossSub = trade.actualLossPct != null
        ? `Max loss on this trade: ${trade.actualLossPct.toFixed(2)}% of active capital${trade.isLongOption ? ' (full premium — option can expire worthless)' : ''}`
        : `Stop loss at ₹${parseFloat(sl).toLocaleString('en-IN')}`;
      items.push({ label: `Stop loss at ₹${parseFloat(sl).toLocaleString('en-IN')}`, sub: lossSub, ok: true });

      if (trade.actualLossPct != null && riskPct != null) {
        const withinLimit = trade.actualLossPct <= riskPct;
        items.push({
          label: withinLimit ? 'Within approved risk per trade' : 'Exceeds approved risk per trade',
          sub: `${trade.actualLossPct.toFixed(2)}% actual risk vs your ${riskPct}% limit`,
          ok: withinLimit,
        });
      } else if (riskPct != null) {
        items.push({ label: `Approved risk per trade: ${riskPct}% of capital`, sub: 'Risk data unavailable for this trade', ok: null });
      }
    }

    // 2. R:R vs profile minimum
    if (rr != null) {
      const rrOk = rr >= minRR;
      items.push({
        label: rrOk ? `R:R ${rr}:1 meets minimum` : `R:R ${rr}:1 below minimum`,
        sub: `Your approved minimum R:R is ${minRR}:1`,
        ok: rrOk,
      });
    }

    // 3. Trade style vs profile
    if (prof.type && trade.tradeVariant) {
      const profileStyle = (prof.type || '').toLowerCase();
      const variant = (trade.tradeVariant || '').toUpperCase();
      const aggressiveSwing = profileStyle === 'very aggressive' && variant === 'INTRADAY';
      const conservativeIntra = profileStyle === 'conservative' && variant === 'INTRADAY';
      if (aggressiveSwing || conservativeIntra) {
        items.push({ label: `${variant} trade matches ${prof.type} profile`, sub: null, ok: true });
      }
    }

    // 4. Position size vs active capital (backend-computed, F&O lot-aware)
    if (trade.deployedPct != null) {
      const sizeOk = trade.deployedPct <= 30;
      items.push({
        label: sizeOk ? 'Position size within limits' : 'Large position — high concentration',
        sub: `${trade.deployedPct.toFixed(1)}% of active capital deployed${trade.foContract ? ' (premium × lots)' : ''}`,
        ok: sizeOk,
      });
    }

    return items;
  })();

  return (
    <div className="space-y-3 pb-28" data-testid="trade-detail-page">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between pt-1 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${colors.accent}15`, border: `1px solid ${colors.accent}30` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.accent }} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>{sym}</h1>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                style={{ background: trade.tradeVariant === 'INTRADAY' ? '#3b82f620' : '#7c3aed20', color: trade.tradeVariant === 'INTRADAY' ? '#60a5fa' : '#a78bfa' }}>
                {trade.tradeVariant}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                style={{ background: isClosed ? `${colors.secondaryText}15` : `${colors.profit}20`, color: isClosed ? colors.secondaryText : colors.profit }}>
                {isClosed ? 'CLOSED' : 'OPEN'}
              </span>
            </div>
            <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{trade.company}</p>
          </div>
        </div>
        {/* action buttons — icon-only on small screens */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {canSocialShare && (
            <button onClick={() => { const s = shareSnap || buildSnap(); setShareSnap(s); setSocialOpen(true); }}
              disabled={capturing}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}
              title="Post">
              <Share2 className="w-3.5 h-3.5" style={{ color: '#818cf8' }} />
            </button>
          )}
          <button onClick={() => { setShareSnap(buildSnap()); setShareOpen(true); }}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}
            title="Forum">
            <Share2 className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
          </button>
        </div>
      </div>

      {/* ── TRADE HERO ── */}
      {isClosed ? (
        /* ── CLOSED TRADE HERO ── */
        <div className="rounded-2xl overflow-hidden"
          style={{ border: `1px solid ${isProfit ? colors.profit : colors.loss}30`, boxShadow: `0 8px 32px ${isProfit ? colors.profit : colors.loss}10` }}>
          {/* colour band top */}
          <div className="px-4 pt-4 pb-3 relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${isProfit ? colors.profit : colors.loss}22 0%, ${colors.cardBg} 100%)` }}>
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${isProfit ? colors.profit : colors.loss}25 0%, transparent 70%)` }} />
            {/* P&L row */}
            <div className="flex items-start justify-between relative">
              <div>
                <p className="text-[10px] font-semibold tracking-widest mb-1" style={{ color: colors.secondaryText }}>TOTAL P&L</p>
                <p className="text-[32px] font-black leading-none" style={{ color: isProfit ? colors.profit : colors.loss }}>
                  {isProfit ? '+' : ''}{formatPrice(trade.pnl)}
                </p>
                <p className="text-[13px] font-semibold mt-1" style={{ color: isProfit ? colors.profit : colors.loss }}>
                  {pct(trade.pnlPct)} return
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {exitQuality && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full font-semibold"
                    style={{ background: `${exitQuality.color}20`, color: exitQuality.color, border: `1px solid ${exitQuality.color}30` }}>
                    {exitQuality.label}
                  </span>
                )}
                {trade.actualR != null && (
                  <div className="text-right">
                    <p className="text-[9px]" style={{ color: colors.secondaryText }}>ACTUAL R</p>
                    <p className="text-[20px] font-black" style={{ color: trade.actualR >= 0 ? colors.profit : colors.loss }}>
                      {trade.actualR >= 0 ? '+' : ''}{trade.actualR.toFixed(2)}R
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* stats row */}
          <div className="grid grid-cols-4 divide-x px-0"
            style={{ borderTop: `1px solid ${colors.border}`, background: colors.cardBg, divideColor: colors.border }}>
            {[
              { label: 'Entry',  val: formatPrice(trade.buyPrice) },
              { label: 'Exit',   val: formatPrice(trade.sellPrice) },
              { label: 'Qty',    val: trade.quantity },
              { label: 'Held',   val: trade.holdingDays != null ? `${trade.holdingDays}d` : '—' },
            ].map(({ label, val }, i) => (
              <div key={label} className="flex flex-col items-center py-3"
                style={{ borderRight: i < 3 ? `1px solid ${colors.border}` : 'none' }}>
                <p className="text-[9px] font-semibold tracking-wide" style={{ color: colors.secondaryText }}>{label}</p>
                <p className="text-[13px] font-bold mt-0.5" style={{ color: colors.primaryText }}>{val}</p>
              </div>
            ))}
          </div>
          {/* date + exit reason + replay row */}
          <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap"
            style={{ borderTop: `1px solid ${colors.border}`, background: colors.cardBg }}>
            <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
            <span className="text-[11px]" style={{ color: colors.secondaryText }}>
              {formatDate(trade.entryDate)} → {formatDate(trade.exitDate)}
            </span>
            {trade.exitReason && (
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold ml-1"
                style={{ background: `${colors.accent}18`, color: colors.accent }}>
                {trade.exitReason}
              </span>
            )}
            {!trade.foContract && (
              <button onClick={() => setShowReplay(s => !s)}
                className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                style={{ background: showReplay ? colors.accent : `${colors.accent}15`, color: showReplay ? '#fff' : colors.accent }}>
                <Play className="w-3 h-3" />{showReplay ? 'Hide' : 'Replay'}
              </button>
            )}
          </div>
        </div>
      ) : (() => {
        /* ── OPEN TRADE HERO ── */
        const entry    = parseFloat(trade.buyPrice || 0);
        const sl       = trade.stopLoss    ? parseFloat(trade.stopLoss)    : null;
        const tgt      = trade.targetPrice ? parseFloat(trade.targetPrice) : null;
        const qty      = trade.quantity || 1;
        const toTarget = tgt ? (tgt - entry) * qty : null;
        const toSL     = sl  ? Math.abs(sl - entry) * qty : null;
        const rrRatio  = (tgt && sl) ? ((tgt - entry) / (entry - sl)).toFixed(1) : null;
        const daysHeld = trade.entryDate
          ? Math.max(0, Math.floor((Date.now() - new Date(trade.entryDate)) / 86400000))
          : null;

        return (
          <div className="rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${colors.accent}30`, boxShadow: `0 8px 32px ${colors.accent}10` }}>
            {/* top band */}
            <div className="px-4 pt-4 pb-3 relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${colors.accent}18 0%, ${colors.cardBg} 100%)` }}>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)` }} />
              <div className="flex items-center gap-2 mb-3 relative">
                <span className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: colors.profit, boxShadow: `0 0 6px ${colors.profit}` }} />
                <p className="text-[11px] font-bold tracking-wide" style={{ color: colors.profit }}>OPEN POSITION</p>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: `${colors.accent}20`, color: colors.accent }}>
                  {trade.tradeVariant}
                </span>
              </div>
              {/* entry price prominent */}
              <div className="flex items-end justify-between relative">
                <div>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>Entry Price</p>
                  <p className="text-[28px] font-black leading-tight" style={{ color: colors.primaryText }}>
                    {formatPrice(entry)}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>
                    {qty} shares · {daysHeld != null ? `${daysHeld} day${daysHeld !== 1 ? 's' : ''} held` : 'entered today'}
                  </p>
                </div>
                {rrRatio && (
                  <div className="text-right">
                    <p className="text-[9px]" style={{ color: colors.secondaryText }}>SETUP R:R</p>
                    <p className="text-[22px] font-black" style={{ color: parseFloat(rrRatio) >= 1.5 ? colors.profit : '#f59e0b' }}>
                      {rrRatio}:1
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* target / SL row */}
            {(toTarget != null || toSL != null) && (
              <div className="grid grid-cols-2 divide-x"
                style={{ borderTop: `1px solid ${colors.border}`, background: colors.cardBg }}>
                {toTarget != null && (
                  <div className="px-4 py-3" style={{ borderRight: `1px solid ${colors.border}` }}>
                    <p className="text-[9px] font-bold tracking-wide mb-1" style={{ color: colors.profit }}>IF TARGET HIT</p>
                    <p className="text-[18px] font-black" style={{ color: colors.profit }}>
                      +₹{toTarget.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>@ ₹{tgt.toFixed(0)}</p>
                  </div>
                )}
                {toSL != null && (
                  <div className="px-4 py-3">
                    <p className="text-[9px] font-bold tracking-wide mb-1" style={{ color: colors.loss }}>IF SL HIT</p>
                    <p className="text-[18px] font-black" style={{ color: colors.loss }}>
                      -₹{toSL.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>@ ₹{sl.toFixed(0)}</p>
                  </div>
                )}
              </div>
            )}

            {/* date + replay row */}
            <div className="flex items-center gap-2 px-4 py-2.5"
              style={{ borderTop: `1px solid ${colors.border}`, background: colors.cardBg }}>
              <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
              <span className="text-[11px]" style={{ color: colors.secondaryText }}>Entered {formatDate(trade.entryDate)}</span>
              {!trade.foContract && (
                <button onClick={() => setShowReplay(s => !s)}
                  className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: showReplay ? colors.accent : `${colors.accent}15`, color: showReplay ? '#fff' : colors.accent }}>
                  <Play className="w-3 h-3" />{showReplay ? 'Hide' : 'Replay'}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {showReplay && !trade.foContract && (
        <TradeReplay symbol={trade.symbol} entryDate={trade.entryDate} exitDate={trade.exitDate}
          entryPrice={trade.buyPrice} exitPrice={trade.sellPrice} stopLoss={trade.stopLoss}
          targetPrice={trade.targetPrice} productType={trade.productType} side="BUY" height={320}
          exitReason={trade.exitReason} onComplete={() => {}} />
      )}

      {exitQuality && isClosed && (
        <div className="rounded-xl px-3 py-2.5 flex items-center gap-3"
          style={{ background: `${exitQuality.color}10`, border: `1px solid ${exitQuality.color}30` }}>
          {exitQuality.label === 'Clean Exit'
            ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: exitQuality.color }} />
            : <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: exitQuality.color }} />}
          <div>
            <p className="text-[12px] font-bold" style={{ color: exitQuality.color }}>{exitQuality.label}</p>
            <p className="text-[11px]" style={{ color: colors.secondaryText }}>{exitQuality.detail}</p>
          </div>
        </div>
      )}

      {/* ══════════ DAY ANALYSIS ══════════ */}
      <Divider label="DAY ANALYSIS" />
      {trade.simDate && (
        <p className="text-[11px] text-center -mt-1" style={{ color: colors.secondaryText }}>
          Entry sim date: <span style={{ color: colors.primaryText, fontWeight: 600 }}>{trade.simDate}</span>
        </p>
      )}

      {/* ── GLOBAL CUES ── */}
      {(prevDayIndices.length > 0 || morningIndices.length > 0 || trade.fiiDii || trade.sentiment) && (
        <Card>
          <SecHead icon={Globe} title="Global Cues" subtitle="What the world looked like on entry day" color="#3b82f6" />

          {/* Previous day: US + Europe */}
          {prevDayIndices.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-bold tracking-widest mb-1.5" style={{ color: colors.secondaryText }}>
                PREV DAY CLOSE — US & EUROPE
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {prevDayIndices.map(idx => {
                  const chg = parseFloat(idx.change_pct || 0);
                  const c = chg > 0 ? colors.profit : chg < 0 ? colors.loss : colors.secondaryText;
                  return (
                    <div key={idx.symbol} className="flex items-center justify-between px-2.5 py-2 rounded-lg"
                      style={{ background: `${c}08`, border: `1px solid ${c}20` }}>
                      <span className="text-[11px] font-medium" style={{ color: colors.primaryText }}>{idx.name?.split(' ')[0] || idx.symbol}</span>
                      <span className="text-[11px] font-bold" style={{ color: c }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</span>
                    </div>
                  );
                })}
              </div>
              <TakeawayBox text={globalTakeaway} color="#3b82f6" />
            </div>
          )}

          {/* Morning: Asia + Macro */}
          {morningIndices.length > 0 && (
            <div className="mb-3">
              <button className="w-full text-left" onClick={() => setGlobalExp(s => !s)}>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold tracking-widest" style={{ color: colors.secondaryText }}>
                    MORNING OF ENTRY — ASIA & MACRO
                  </p>
                  {globalExp
                    ? <ChevronUp className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
                    : <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />}
                </div>
              </button>
              {globalExp && (
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {morningIndices.map(idx => {
                    const chg = parseFloat(idx.change_pct || 0);
                    const c = chg > 0 ? colors.profit : chg < 0 ? colors.loss : colors.secondaryText;
                    return (
                      <div key={idx.symbol} className="flex items-center justify-between px-2.5 py-2 rounded-lg"
                        style={{ background: `${c}08`, border: `1px solid ${c}20` }}>
                        <span className="text-[11px] font-medium" style={{ color: colors.primaryText }}>{idx.name?.split(' ')[0] || idx.symbol}</span>
                        <span className="text-[11px] font-bold" style={{ color: c }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* FII / DII */}
          {trade.fiiDii && (
            <div className="mb-1">
              <p className="text-[10px] font-bold tracking-widest mb-1.5" style={{ color: colors.secondaryText }}>FII / DII FLOWS</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'FII Net', val: fii.fii_net, ls: [fii.fii_total_long, fii.fii_total_short] },
                  { label: 'DII Net', val: fii.dii_net, ls: [fii.dii_total_long, fii.dii_total_short] },
                ].map(({ label, val, ls }) => val != null && (
                  <div key={label} className="p-2.5 rounded-xl"
                    style={{ background: val >= 0 ? `${colors.profit}10` : `${colors.loss}10`, border: `1px solid ${val >= 0 ? colors.profit : colors.loss}25` }}>
                    <p className="text-[10px]" style={{ color: colors.secondaryText }}>{label}</p>
                    <p className="text-[14px] font-bold" style={{ color: val >= 0 ? colors.profit : colors.loss }}>{cr(val)}</p>
                    {ls[0] != null && (
                      <p className="text-[9px] mt-0.5" style={{ color: colors.secondaryText }}>
                        L: {cr(ls[0])} · S: {cr(ls[1])}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <TakeawayBox text={fiiTakeaway} color={fii.fii_net >= 0 ? colors.profit : colors.loss} />
            </div>
          )}

          {/* Sentiment */}
          {trade.sentiment?.label && (() => {
            const sc = trade.sentiment.label.includes('RISK_ON') ? colors.profit : trade.sentiment.label.includes('RISK_OFF') ? colors.loss : '#f59e0b';
            return (
              <div className="mt-3 flex items-center justify-between px-3 py-2.5 rounded-xl"
                style={{ background: `${sc}10`, border: `1px solid ${sc}25` }}>
                <div>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>Market Sentiment</p>
                  <p className="text-[13px] font-bold" style={{ color: sc }}>{trade.sentiment.label.replace(/_/g,' ')}</p>
                  {trade.sentiment.article_count && <p className="text-[10px]" style={{ color: colors.secondaryText }}>{trade.sentiment.article_count} news articles</p>}
                </div>
                {trade.sentiment.score != null && (
                  <p className="text-[22px] font-black" style={{ color: sc }}>{trade.sentiment.score}</p>
                )}
              </div>
            );
          })()}
        </Card>
      )}

      {/* ── MARKET REGIME ── */}
      {regime.gap_type && (
        <Card>
          <SecHead icon={Flame} title="Market Regime" subtitle="How the market opened and moved" color="#f59e0b" />

          {/* key pills — only meaningful ones */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {regime.global_bias && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{ background: `${biasColor}20`, color: biasColor }}>
                {regime.global_bias.toUpperCase()} BIAS
              </span>
            )}
            {regime.gap_type && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{ background: `${gapColor}20`, color: gapColor }}>
                {(regime.gap_label || regime.gap_type.replace(/_/g,' ')).toUpperCase()}
                {regime.gap_value != null ? ` ${pct(regime.gap_value)}` : ''}
              </span>
            )}
            {regime.day_type && regime.day_type !== 'Conflict Day' && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{ background: `${colors.accent}20`, color: colors.accent }}>
                {regime.day_type.toUpperCase()}
              </span>
            )}
            {regime.conflict && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{ background: `${colors.loss}20`, color: colors.loss }}>
                CONFLICT DAY
              </span>
            )}
            {isClosed && regime.match != null && trade.exitReason !== 'EOD' && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{ background: regime.match ? `${colors.profit}20` : `${colors.loss}20`,
                         color: regime.match ? colors.profit : colors.loss }}>
                {regime.match ? 'FOLLOWED ✓' : 'REVERSED ✗'}
              </span>
            )}
          </div>

          {/* expected / nifty grid — hide actual_dir for open trades */}
          <div className={`grid gap-2 mb-3 ${isClosed ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <MiniCell label="Expected" value={regime.expected_dir === 'conflict' ? 'CONFLICT' : regime.expected_dir?.toUpperCase() || '—'}
              color={regime.expected_dir === 'bull' ? colors.profit : regime.expected_dir === 'bear' ? colors.loss : '#f59e0b'} />
            {isClosed && (
              <MiniCell label="Actual Close" value={regime.actual_dir?.toUpperCase() || '—'}
                color={regime.actual_dir === 'bull' ? colors.profit : regime.actual_dir === 'bear' ? colors.loss : null} />
            )}
            <MiniCell label={isClosed ? 'Nifty Move' : 'Nifty Open'}
              value={isClosed && regime.nifty_change_pct != null ? pct(regime.nifty_change_pct) : regime.nifty_open ? `₹${parseFloat(regime.nifty_open).toFixed(0)}` : '—'}
              color={isClosed && regime.nifty_change_pct != null ? (regime.nifty_change_pct >= 0 ? colors.profit : colors.loss) : null} />
          </div>

          {/* bull/bear signals */}
          {(regime.bull_signals != null || regime.bear_signals != null) && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              {regime.bull_signals != null && (
                <div className="p-2.5 rounded-xl" style={{ background: `${colors.profit}10`, border: `1px solid ${colors.profit}25` }}>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>Bull Signals</p>
                  <p className="text-[18px] font-black" style={{ color: colors.profit }}>{regime.bull_signals}</p>
                  {regime.bull_hit_rate != null && <p className="text-[10px]" style={{ color: colors.secondaryText }}>{regime.bull_hit_rate}% hit rate</p>}
                </div>
              )}
              {regime.bear_signals != null && (
                <div className="p-2.5 rounded-xl" style={{ background: `${colors.loss}10`, border: `1px solid ${colors.loss}25` }}>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>Bear Signals</p>
                  <p className="text-[18px] font-black" style={{ color: colors.loss }}>{regime.bear_signals}</p>
                  {regime.bear_hit_rate != null && <p className="text-[10px]" style={{ color: colors.secondaryText }}>{regime.bear_hit_rate}% hit rate</p>}
                </div>
              )}
            </div>
          )}

          {/* sectors */}
          {(regime.sector_leader || regime.sector_laggard) && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              {regime.sector_leader  && <MiniCell label="Leading Sector"  value={regime.sector_leader}  color={colors.profit} />}
              {regime.sector_laggard && <MiniCell label="Lagging Sector" value={regime.sector_laggard} color={colors.loss} />}
            </div>
          )}

          {/* combo stats */}
          {combo.combo_label && combo.hit_rate != null && combo.total_days >= 3 && (
            <div className="px-3 py-2.5 rounded-xl mb-3"
              style={{ background: `${comboColor}10`, border: `1px solid ${comboColor}25` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-bold" style={{ color: comboColor }}>{combo.combo_label}</p>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                  style={{ background: `${comboColor}20`, color: comboColor }}>{combo.mode}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-[22px] font-black" style={{ color: comboColor }}>{combo.hit_rate}%</p>
                <p className="text-[11px]" style={{ color: colors.secondaryText }}>follow-through over {combo.total_days} days</p>
              </div>
            </div>
          )}

          {/* best entry */}
          {regime.best_entry && (
            <div className="flex items-center gap-1.5 mb-3">
              <Clock className="w-3 h-3" style={{ color: colors.secondaryText }} />
              <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                Best entry window: <span style={{ color: colors.primaryText, fontWeight: 600 }}>{regime.best_entry}</span>
              </p>
            </div>
          )}

          {/* verdict — summary at the bottom */}
          {regime.verdict_sentence && (
            <div className="px-3 py-2.5 rounded-xl"
              style={{ background: `${colors.accent}08`, border: `1px solid ${colors.accent}18` }}>
              <p className="text-[10px] font-bold tracking-wide mb-1" style={{ color: colors.secondaryText }}>DAY SUMMARY</p>
              <p className="text-[12px] leading-relaxed font-medium" style={{ color: colors.primaryText }}>
                {regime.verdict_sentence}
              </p>
              {regime.fo_performance?.directionWinner && (
                <p className="text-[11px] mt-1" style={{ color: colors.secondaryText }}>
                  Options: {regime.fo_performance.directionWinner}
                </p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ══════════ TRADE ANALYSIS ══════════ */}
      <Divider label="TRADE ANALYSIS" />

      {/* ── SCANNER SIGNALS ── */}
      {scanners.length > 0 && (() => {
        // Classify each signal by name — direction matters more than strength
        const BEARISH_NAMES = ['BEARISH','DEATH_CROSS','OVERBOUGHT','BREAKDOWN','BELOW_SMA','RESISTANCE','SHORT'];
        const CAUTION_NAMES = ['OVERBOUGHT','RSI_ABOVE_70','REVERSAL','DIVERGENCE','EXHAUSTION'];
        const classify = s => {
          const n = s.scanner.toUpperCase();
          if (CAUTION_NAMES.some(k => n.includes(k))) return 'caution';
          if (BEARISH_NAMES.some(k => n.includes(k)))  return 'bearish';
          return 'bullish';
        };
        const bullish = scanners.filter(s => classify(s) === 'bullish');
        const bearish = scanners.filter(s => classify(s) === 'bearish');
        const caution = scanners.filter(s => classify(s) === 'caution');

        const strongBull = bullish.filter(s => s.strength === 'strong').length;
        const strongBear = bearish.filter(s => s.strength === 'strong').length;

        let takeaway = null, takeawayColor = null;
        if (strongBull > 0 && strongBear > 0) {
          takeaway = `Mixed picture — ${strongBull} strong bullish and ${strongBear} strong bearish signals. Lower conviction setup for a long.`;
          takeawayColor = '#f59e0b';
        } else if (strongBear > 0 && bullish.length === 0) {
          takeaway = `${strongBear} strong bearish signal${strongBear>1?'s':''} with no bullish backing — technical setup was against this long entry.`;
          takeawayColor = colors.loss;
        } else if (strongBull > 0 && bearish.length === 0) {
          takeaway = `${strongBull} strong bullish signal${strongBull>1?'s':''} aligned with your long — good technical backing.`;
          takeawayColor = colors.profit;
        } else if (bearish.length > bullish.length) {
          takeaway = `More bearish signals (${bearish.length}) than bullish (${bullish.length}) on entry day — technical weight was against the trade.`;
          takeawayColor = '#f59e0b';
        }

        const SignalPill = ({ s, dir }) => {
          const c = dir === 'bullish' ? colors.profit : dir === 'bearish' ? colors.loss : '#f59e0b';
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold"
              style={{ background: `${c}15`, color: c, border: `1px solid ${c}25` }}>
              {s.scanner.replace(/_/g,' ')}
              {s.strength === 'strong' && <span style={{ fontSize: 9 }}>★</span>}
            </span>
          );
        };

        return (
          <Card>
            <SecHead icon={Activity} title="Scanner Signals" subtitle={`${sym} on entry day`} color="#22d3ee" />
            {bullish.length > 0 && (
              <div className="mb-2.5">
                <p className="text-[9px] font-bold tracking-widest mb-1.5" style={{ color: colors.profit }}>BULLISH</p>
                <div className="flex flex-wrap gap-1.5">
                  {bullish.map((s,i) => <SignalPill key={i} s={s} dir="bullish" />)}
                </div>
              </div>
            )}
            {bearish.length > 0 && (
              <div className="mb-2.5">
                <p className="text-[9px] font-bold tracking-widest mb-1.5" style={{ color: colors.loss }}>BEARISH</p>
                <div className="flex flex-wrap gap-1.5">
                  {bearish.map((s,i) => <SignalPill key={i} s={s} dir="bearish" />)}
                </div>
              </div>
            )}
            {caution.length > 0 && (
              <div className="mb-2.5">
                <p className="text-[9px] font-bold tracking-widest mb-1.5" style={{ color: '#f59e0b' }}>CAUTION</p>
                <div className="flex flex-wrap gap-1.5">
                  {caution.map((s,i) => <SignalPill key={i} s={s} dir="caution" />)}
                </div>
              </div>
            )}
            {takeaway && <TakeawayBox text={takeaway} color={takeawayColor} />}
          </Card>
        );
      })()}

      {/* ── PRE-TRADE CHECKLIST ── */}
      {(checklist.confidence || de.grade || checklist.trade_reason) && (
        <Card>
          <SecHead icon={BookOpen} title="Pre-Trade Decision" subtitle="What you thought before entering" color="#a78bfa" />
          <div className="grid grid-cols-3 gap-2 mb-3">
            {checklist.confidence != null && (
              <div className="col-span-1 p-2.5 rounded-xl"
                style={{ background: checklist.confidence >= 7 ? `${colors.profit}10` : checklist.confidence >= 5 ? '#f59e0b10' : `${colors.loss}10`,
                         border: `1px solid ${checklist.confidence >= 7 ? colors.profit : checklist.confidence >= 5 ? '#f59e0b' : colors.loss}25` }}>
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>Confidence</p>
                <p className="text-[22px] font-black leading-tight"
                  style={{ color: checklist.confidence >= 7 ? colors.profit : checklist.confidence >= 5 ? '#f59e0b' : colors.loss }}>
                  {checklist.confidence}<span className="text-[12px]">/10</span>
                </p>
              </div>
            )}
            {de.grade && (
              <div className="col-span-1 p-2.5 rounded-xl" style={{ background: `${deGradeColor}10`, border: `1px solid ${deGradeColor}25` }}>
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>DE Grade</p>
                <p className="text-[22px] font-black" style={{ color: deGradeColor }}>{de.grade}</p>
              </div>
            )}
            {de.regime_label && (
              <div className="col-span-1 p-2.5 rounded-xl" style={{ background: colors.inputBg }}>
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>Setup Type</p>
                <p className="text-[10px] font-bold leading-tight mt-0.5" style={{ color: colors.primaryText }}>{de.regime_label}</p>
              </div>
            )}
          </div>
          {checklist.trade_reason && (
            <div className="p-3 rounded-xl" style={{ background: `${colors.accent}08`, border: `1px solid ${colors.accent}15` }}>
              <p className="text-[10px] font-medium mb-1" style={{ color: colors.secondaryText }}>Your reason for this trade</p>
              <p className="text-[12px] italic leading-relaxed" style={{ color: colors.primaryText }}>"{checklist.trade_reason}"</p>
            </div>
          )}
        </Card>
      )}

      {/* ── TRADE SETUP ── */}
      <Card>
        <SecHead icon={Target} title="Trade Setup" color={colors.accent} />
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-2.5 rounded-xl" style={{ background: `${colors.loss}10`, border: `1px solid ${colors.loss}25` }}>
            <div className="flex items-center gap-1 mb-0.5">
              <Shield className="w-3 h-3" style={{ color: colors.loss }} />
              <p className="text-[10px]" style={{ color: colors.secondaryText }}>Stop Loss</p>
            </div>
            <p className="text-[14px] font-bold" style={{ color: trade.stopLoss ? colors.loss : colors.secondaryText }}>
              {trade.stopLoss ? formatPrice(trade.stopLoss) : 'Not set ⚠'}
            </p>
          </div>
          <div className="p-2.5 rounded-xl" style={{ background: `${colors.profit}10`, border: `1px solid ${colors.profit}25` }}>
            <div className="flex items-center gap-1 mb-0.5">
              <Target className="w-3 h-3" style={{ color: colors.profit }} />
              <p className="text-[10px]" style={{ color: colors.secondaryText }}>Target</p>
            </div>
            <p className="text-[14px] font-bold" style={{ color: trade.targetPrice ? colors.profit : colors.secondaryText }}>
              {trade.targetPrice ? formatPrice(trade.targetPrice) : 'Not set'}
            </p>
          </div>
        </div>

        {rr != null && (
          <div className="mb-3 px-3 py-2 rounded-xl flex items-center justify-between"
            style={{ background: `${rr >= 1.5 ? colors.profit : colors.loss}10`, border: `1px solid ${rr >= 1.5 ? colors.profit : colors.loss}25` }}>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>R:R Ratio</p>
            <p className="text-[16px] font-black" style={{ color: rr >= 1.5 ? colors.profit : colors.loss }}>{rr}:1</p>
          </div>
        )}

        {trade.strategyTags?.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-1 mb-1.5">
              <Tag className="w-3 h-3" style={{ color: colors.accent }} />
              <p className="text-[10px] font-bold tracking-wide" style={{ color: colors.secondaryText }}>STRATEGY TAGS</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {trade.strategyTags.map((tag, i) => (
                <span key={i} className="px-2.5 py-1 rounded-lg text-[11px] font-medium"
                  style={{ background: `${colors.accent}18`, color: colors.accent, border: `1px solid ${colors.accent}25` }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {trade.notes && (
          <div className="p-3 rounded-xl" style={{ background: colors.inputBg }}>
            <div className="flex items-center gap-1 mb-1.5">
              <StickyNote className="w-3 h-3" style={{ color: colors.secondaryText }} />
              <p className="text-[10px] font-bold tracking-wide" style={{ color: colors.secondaryText }}>JOURNAL NOTE</p>
            </div>
            <p className="text-[12px] italic leading-relaxed" style={{ color: colors.primaryText }}>"{trade.notes}"</p>
          </div>
        )}
      </Card>

      {/* ── F&O CONTRACT ── */}
      {trade.foContract && (
        <Card>
          <SecHead icon={BarChart2} title="F&O Contract" subtitle="Contract details at entry" color="#f59e0b" />
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Underlying', val: trade.foContract.underlying },
              { label: 'Type', val: trade.foContract.contract_type },
              { label: 'Strike', val: trade.foContract.strike ? `₹${trade.foContract.strike.toLocaleString('en-IN')}` : '—' },
              { label: 'Moneyness', val: trade.foContract.moneyness || '—' },
              { label: 'Expiry', val: trade.foContract.expiry || '—' },
              { label: 'DTE', val: trade.foContract.days_to_expiry != null ? `${trade.foContract.days_to_expiry}d` : '—' },
            ].map(({ label, val }) => (
              <div key={label} className="p-2.5 rounded-xl" style={{ background: colors.inputBg }}>
                <p className="text-[10px]" style={{ color: colors.secondaryText }}>{label}</p>
                <p className="text-[12px] font-bold mt-0.5" style={{ color: colors.primaryText }}>{val}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══════════ DISCIPLINE & RISK ══════════ */}
      <Divider label="DISCIPLINE & RISK" />

      {/* ── DISCIPLINE SCORE ── */}
      {disc.score != null && (
        <Card>
          <SecHead icon={Shield} title="Discipline Score" color="#16C784" />
          <div className="flex items-center gap-4 mb-4">
            <div className="relative w-20 h-20 flex-shrink-0">
              <svg viewBox="0 0 80 80" className="w-20 h-20 -rotate-90">
                <circle cx="40" cy="40" r="32" fill="none" strokeWidth="7" stroke={colors.border} />
                <circle cx="40" cy="40" r="32" fill="none" strokeWidth="7"
                  stroke={disc.score >= 60 ? colors.profit : disc.score >= 40 ? '#f59e0b' : colors.loss}
                  strokeDasharray={`${(disc.score/100)*201} 201`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[18px] font-black" style={{ color: disc.score >= 60 ? colors.profit : disc.score >= 40 ? '#f59e0b' : colors.loss }}>{disc.score}</span>
                <span className="text-[9px]" style={{ color: colors.secondaryText }}>/100</span>
              </div>
            </div>
            <div className="flex-1">
              <CheckRow label="Stop Loss Set" ok={disc.sl_placed} />
              <CheckRow label="Trade Tagged" ok={disc.tagged} />
              <CheckRow label="Journaled" ok={disc.journaled} />
              <CheckRow label="R:R ≥ 1.5" ok={rr != null && rr >= 1.5} value={rr != null ? `${rr}:1` : null} />
            </div>
          </div>
          {disc.exit_quality && isClosed && (
            <div className="flex items-center justify-between px-3 py-2 rounded-xl mb-2"
              style={{ background: colors.inputBg }}>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>Exit type</p>
              <span className="text-[12px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: disc.exit_quality === 'plan' ? `${colors.profit}20` : disc.exit_quality === 'eod' ? '#f59e0b20' : `${colors.loss}15`,
                         color: disc.exit_quality === 'plan' ? colors.profit : disc.exit_quality === 'eod' ? '#f59e0b' : colors.loss }}>
                {disc.exit_quality === 'plan' ? 'On Plan' : disc.exit_quality === 'eod' ? 'Force Exit (EOD)' : 'Manual'}
              </span>
            </div>
          )}
          {disc.violations?.length > 0 && (
            <div className="px-3 py-2.5 rounded-xl" style={{ background: `${colors.loss}08`, border: `1px solid ${colors.loss}20` }}>
              <p className="text-[10px] font-bold tracking-wide mb-1.5" style={{ color: colors.loss }}>VIOLATIONS</p>
              {disc.violations.map((v, i) => (
                <div key={i} className="flex items-start gap-1.5 mb-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: colors.loss }} />
                  <p className="text-[11px]" style={{ color: colors.primaryText }}>{v}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ── RISK RULE CHECK ── */}
      {riskChecks.length > 0 && (
        <Card>
          <SecHead icon={AlertTriangle} title="Risk Rule Check" subtitle="Did this trade respect your approved limits?" color="#f59e0b" />
          <div style={{ borderTop: `1px solid ${colors.border}` }}>
            {riskChecks.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 py-2.5" style={{ borderBottom: `1px solid ${colors.border}` }}>
                {item.ok === true  && <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: colors.profit }} />}
                {item.ok === false && <XCircle      className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: colors.loss }} />}
                {item.ok === null  && <Info         className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />}
                <div>
                  <p className="text-[12px] font-medium" style={{ color: item.ok === false ? colors.loss : colors.primaryText }}>{item.label}</p>
                  {item.sub && <p className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>{item.sub}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ══════════ AI REVIEW ══════════ */}
      <Divider label="AI REVIEW" />

      <div className="rounded-2xl p-4"
        style={{ background: `linear-gradient(135deg,#7c3aed18 0%,${colors.cardBg} 100%)`,
                 border: '1px solid #7c3aed30', boxShadow: '0 8px 32px rgba(124,58,237,0.08)' }}>
        <SecHead icon={Brain} title="AI Trade Review" color="#a78bfa" />
        {aiReview ? (
          <div className="space-y-2">
            {aiReview.split('\n').filter(Boolean).map((line, i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl"
                style={{ background: i === 0 ? '#7c3aed14' : colors.inputBg }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: '#7c3aed25' }}>
                  <span className="text-[9px] font-black" style={{ color: '#a78bfa' }}>{i+1}</span>
                </div>
                <p className="text-[12px] leading-relaxed" style={{ color: colors.primaryText }}>{line}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-center py-4" style={{ color: colors.secondaryText }}>Analysis unavailable</p>
        )}
        <p className="text-[10px] mt-3 text-center" style={{ color: colors.secondaryText }}>Rule-based coach · Educational only</p>
      </div>

      {/* ── MODALS ── */}
      {shareSnap && (
        <ShareToForumModal isOpen={shareOpen} onClose={() => setShareOpen(false)}
          attachments={[{ attach_type: 'trade_story', snapshot: shareSnap }]}
          defaultTitle={`${sym} Trade Story${trade.pnl!=null?' — '+(trade.pnl>=0?'+':'')+'₹'+Math.abs(Math.round(trade.pnl)).toLocaleString('en-IN'):''}`}
          defaultLifecycle={trade.pnl >= 0 ? 'profit' : 'loss'} />
      )}

      {/* off-screen capture target */}
      <div style={{ position: 'fixed', left: -9999, top: 0, width: 1080, height: 1350, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <TradeShareCard_v2 trade={trade} snap={shareSnap} cardRef={shareCardRefV2} />
      </div>

      {canSocialShare && socialOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor:'rgba(0,0,0,0.7)' }}
          onClick={() => setSocialOpen(false)}>
          <div className="w-full max-w-sm rounded-t-2xl p-5 space-y-4"
            style={{ background: colors.cardBg, border:`1px solid ${colors.border}` }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold" style={{ color:colors.primaryText }}>Share Trade Card</p>
              <button onClick={() => setSocialOpen(false)}><X className="w-4 h-4" style={{ color:colors.secondaryText }} /></button>
            </div>

            {/* preview */}
            {(() => {
              const scale = 300 / 1080;
              const PH    = Math.round(1350 * scale);
              return (
                <div style={{ width: '100%', height: PH, overflow: 'hidden', borderRadius: 10, background: '#08090f' }}>
                  <div style={{ width: 1080, height: 1350, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                    <TradeShareCard_v2 trade={trade} snap={shareSnap} />
                  </div>
                </div>
              );
            })()}

            {/* Share + Download buttons */}
            <div className="flex gap-3">
              <button onClick={async () => {
                  setCapturing(true);
                  try {
                    const { toPng } = await import('html-to-image');
                    await document.fonts.ready;
                    const dataUrl = await toPng(shareCardRefV2.current, { cacheBust:true, pixelRatio:2, backgroundColor:'#020609' });
                    const blob = await (await fetch(dataUrl)).blob();
                    const file = new File([blob], `${sym}-trade.png`, { type:'image/png' });
                    if (navigator.canShare && navigator.canShare({ files:[file] })) {
                      await navigator.share({ files:[file], title:`${sym} Trade Analysis` });
                    } else {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href=url; a.download=`${sym}-trade.png`; a.click();
                      URL.revokeObjectURL(url);
                    }
                  } catch(e) {} finally { setCapturing(false); }
                }}
                disabled={capturing}
                className="flex-1 py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
                style={{ background:colors.accent, color:'#fff', opacity:capturing?0.6:1 }}>
                {capturing ? <Loader className="w-4 h-4 animate-spin"/> : <Share2 className="w-4 h-4"/>}
                {capturing ? 'Preparing…' : 'Share'}
              </button>
              <button onClick={async () => {
                  setCapturing(true);
                  try {
                    const { toPng } = await import('html-to-image');
                    await document.fonts.ready;
                    const dataUrl = await toPng(shareCardRefV2.current, { cacheBust:true, pixelRatio:2, backgroundColor:'#020609' });
                    const blob = await (await fetch(dataUrl)).blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href=url; a.download=`${sym}-trade.png`; a.click();
                    URL.revokeObjectURL(url);
                  } catch(e) {} finally { setCapturing(false); setSocialOpen(false); }
                }}
                disabled={capturing}
                className="flex-1 py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
                style={{ background:`${colors.accent}20`, color:colors.accent, border:`1px solid ${colors.accent}40`, opacity:capturing?0.6:1 }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
